import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Maximize2, X } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CATEGORY_COLORS } from "@/components/CategoryIcon";

function placeIcon(color, label, slug) {
  return L.divIcon({
    className: "csra-pin",
    html: `<div data-testid="map-pin-${slug || ""}" style="background:${color};width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:12px;line-height:1;">${label || ""}</span></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });
}

const userIcon = L.divIcon({
  className: "csra-user-pin",
  html: `<div data-testid="map-user-pin" style="position:relative;width:18px;height:18px;">
    <span style="position:absolute;inset:-6px;border-radius:50%;background:rgba(37,99,235,.35);animation:csra-ping 1.6s ease-out infinite;"></span>
    <span style="position:absolute;inset:0;border-radius:50%;background:#2563eb;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);"></span></div>
  <style>@keyframes csra-ping{0%{transform:scale(1);opacity:.7}100%{transform:scale(2.2);opacity:0}}</style>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 14);
    else map.fitBounds(points, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

function InvalidateOnResize({ trigger }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [map, trigger]);
  return null;
}

/**
 * Interactive map on real OpenStreetMap tiles. Color-coded category pins,
 * optional numbered route line, a "you are here" pin, and a full-screen toggle.
 */
export function MapView({ places, height = 460, route = false, userLoc = null }) {
  const [expanded, setExpanded] = useState(false);
  const withCoords = useMemo(() => places.filter((p) => p.lat && p.lng), [places]);

  const points = useMemo(() => {
    const pts = withCoords.map((p) => [p.lat, p.lng]);
    if (userLoc) pts.push([userLoc.lat, userLoc.lng]);
    return pts;
  }, [withCoords, userLoc]);

  const routeLine = route ? withCoords.map((p) => [p.lat, p.lng]) : null;

  if (!withCoords.length && !userLoc) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-border bg-muted text-muted-foreground" style={{ height }}>
        No mappable places yet.
      </div>
    );
  }

  const center = points[0] || [33.5604, -81.7196]; // Aiken fallback

  return (
    <div
      className={expanded ? "fixed inset-0 z-[70]" : "relative overflow-hidden rounded-2xl border border-border"}
      style={{ height: expanded ? "100dvh" : height }}
      data-testid="map-view"
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid={expanded ? "map-close-fullscreen" : "map-open-fullscreen"}
        aria-label={expanded ? "Close full-screen map" : "Open full-screen map"}
        className="absolute right-3 z-[1000] flex h-10 w-10 items-center justify-center rounded-full bg-card/95 text-foreground shadow-lg backdrop-blur-sm transition-transform hover:scale-105"
        style={{ top: expanded ? "max(0.75rem, env(safe-area-inset-top))" : "0.75rem" }}
      >
        {expanded ? <X size={18} /> : <Maximize2 size={16} />}
      </button>

      <MapContainer center={center} zoom={12} scrollWheelZoom={expanded} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        <InvalidateOnResize trigger={expanded} />

        {routeLine && routeLine.length > 1 && (
          <Polyline positions={routeLine} pathOptions={{ color: "#c0492b", weight: 3, dashArray: "6 8" }} />
        )}

        {withCoords.map((p, i) => {
          const color = route ? "#c0492b" : (CATEGORY_COLORS[p.category] || "#7A6A55");
          return (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={placeIcon(color, route ? i + 1 : "", p.slug)}>
              <Popup>
                <div className="min-w-[140px]">
                  <p className="font-semibold">{p.name}</p>
                  {p.category && <p className="text-xs text-muted-foreground">{p.category}</p>}
                  <Link to={`/place/${p.slug}`} data-testid={`map-pin-${p.slug}`} className="mt-1 inline-block text-sm font-semibold text-primary">
                    View details →
                  </Link>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {userLoc && <Marker position={[userLoc.lat, userLoc.lng]} icon={userIcon}><Popup>You are here</Popup></Marker>}
      </MapContainer>
    </div>
  );
}
